import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionItemProps, Heading } from '@appsumo/dorado-react';
import { useRouter } from 'next/router';

import {
  FTF_MAX_POSTS_TO_DISPLAY,
  FTF_POST_NUMBER_HASH_PREFIX,
} from '~/constants/product';
import { formatToHumanReadableDate } from '~/lib/util';
import type { FTFPost } from '~/types/product';

import FTFPostsPagination from './FTFPostsPagination';
import FTFPostAuthor from './FTFPostAuthor';

export type FTFPostsProps = {
  companyName: string;
  posts: FTFPost[];
};

export default memo(function FTFPosts({
  companyName,
  posts,
}: Readonly<FTFPostsProps>) {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  );

  // Update the selected post index when the hash changes.
  useEffect(() => {
    if (router.isReady) {
      const hash = router.asPath.split('#')[1];
      if (hash?.startsWith(FTF_POST_NUMBER_HASH_PREFIX)) {
        const postNumber = parseInt(hash.split('-')[2], 10);

        if (postNumber >= 1 && postNumber <= posts.length) {
          const index = postNumber - 1; // Convert to zero-based index.
          const page = Math.floor(index / FTF_MAX_POSTS_TO_DISPLAY) + 1;
          setCurrentPage(page);
          setSelectedIndex(index % FTF_MAX_POSTS_TO_DISPLAY);
        }
      }
    }
  }, [router.isReady, router.asPath, posts.length]);

  const totalPages = useMemo(
    () => Math.ceil(posts.length / FTF_MAX_POSTS_TO_DISPLAY),
    [posts.length],
  );

  const { startIndex, endIndex, paginatedPosts } = useMemo(() => {
    const startIndex = (currentPage - 1) * FTF_MAX_POSTS_TO_DISPLAY;
    const endIndex = Math.min(
      currentPage * FTF_MAX_POSTS_TO_DISPLAY,
      posts.length,
    );
    const paginatedPosts = posts.slice(startIndex, endIndex);
    return { startIndex, endIndex, paginatedPosts };
  }, [currentPage, posts]);

  const updatePageHash = useCallback(
    (index: number) => {
      const overallIndex = startIndex + index;
      window.location.hash = `${FTF_POST_NUMBER_HASH_PREFIX}${
        overallIndex + 1
      }`;
    },
    [startIndex],
  );

  const handlePageChange = useCallback(
    (factor: number) => {
      setCurrentPage((prevPage) =>
        Math.max(1, Math.min(prevPage + factor, totalPages)),
      );
    },
    [totalPages],
  );

  const postsToDisplay = useMemo((): AccordionItemProps[] => {
    return paginatedPosts.map((post) => {
      let date = '';
      let prefix = '';
      if (post.created === post.modified) {
        date = post.created;
        prefix = 'Posted';
      } else {
        date = post.modified;
        prefix = 'Updated';
      }

      const founderName =
        [post.user.first_name, post.user.last_name].join(' ').trim() ||
        post.user.username ||
        '';
      return {
        title: post.title,
        subtitle: `${prefix} ${formatToHumanReadableDate(date, {
          month: 'short',
        })}`,
        content: (
          <div className="flex flex-col gap-4">
            <div
              className="prose [word-break:break-word]"
              dangerouslySetInnerHTML={{ __html: post.message }}
            />
            <FTFPostAuthor
              avatar={post.user.avatar}
              displayName={founderName}
              username={post.user.username}
              emailVerified={post.user.email_verified}
              title={post.user.title}
            />
          </div>
        ),
      };
    });
  }, [paginatedPosts]);

  return (
    <div className="flex flex-col gap-4 grid-in-body">
      <Heading.H4 className="text-xl">Updates from {companyName}</Heading.H4>
      <Accordion
        items={postsToDisplay}
        className="mt-0 w-full [&>div]:bg-white"
        selectedIndex={selectedIndex}
        onClick={updatePageHash}
        resetOpenIndexAfterItemsChange
      />
      <FTFPostsPagination
        from={startIndex + 1}
        to={endIndex}
        length={posts.length}
        disablePrev={currentPage === 1}
        disableNext={currentPage === totalPages}
        onClick={handlePageChange}
      />
    </div>
  );
});
